/**
 * 离线写操作队列
 * - 网络失败的写操作 (POST/PATCH/DELETE) 入队, 存 localStorage
 * - 网络恢复后按顺序重放
 * - GET 失败不入队, 让上层处理 (重连后会触发 refresh)
 *
 * 简化假设:
 * - 单标签页, 不考虑多标签页同时离线编辑
 * - 队列容量 100, 满了丢最早的
 * - 重放时如果又失败, 重新入队 (避免丢数据)
 */

interface QueueItem {
  method: 'POST' | 'PATCH' | 'DELETE';
  path: string;
  body?: unknown;
  ts: number;
  attempts: number;
}

const STORAGE_KEY = 'todo_offline_queue';
const MAX_QUEUE = 100;

function readQueue(): QueueItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as QueueItem[];
  } catch {
    return [];
  }
}

function writeQueue(items: QueueItem[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // localStorage 满了或不可用, 静默
  }
}

export const offlineQueue = {
  list(): QueueItem[] {
    return readQueue();
  },

  size(): number {
    return readQueue().length;
  },

  enqueue(method: QueueItem['method'], path: string, body?: unknown): void {
    const items = readQueue();
    items.push({ method, path, body, ts: Date.now(), attempts: 0 });
    // 超出容量, 丢最早的
    while (items.length > MAX_QUEUE) items.shift();
    writeQueue(items);
    // 通知监听者 (manager 会更新 store)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('todo:queue-changed', { detail: items.length })
      );
    }
  },

  clear(): void {
    writeQueue([]);
  },

  /**
   * 重放队列 (网络恢复时调)
   * @param fetcher 实际发送函数, 返回 Response
   * @returns { ok: number, failed: number }
   */
  async replay(
    fetcher: (item: QueueItem) => Promise<{ ok: boolean; shouldRetry: boolean }>
  ): Promise<{ ok: number; failed: number }> {
    const items = readQueue();
    if (items.length === 0) return { ok: 0, failed: 0 };

    let ok = 0;
    let failed = 0;
    const remaining: QueueItem[] = [];

    for (const item of items) {
      const r = await fetcher(item);
      if (r.ok) {
        ok++;
      } else if (r.shouldRetry) {
        // 网络问题, 留在队列
        remaining.push({ ...item, attempts: item.attempts + 1 });
        failed++;
      } else {
        // 业务错误 (4xx), 丢弃
        failed++;
      }
    }

    writeQueue(remaining);
    return { ok, failed };
  },
};